<template>
  <ion-page>
    <ion-content class="main-background" :fullscreen="true">
      <!-- <div class="page-container"> -->
        <h1 class="header-title ion-text-center white-bold">
          Образование
        </h1>

        <!-- Video Player Overlay -->
        <div class="video-overlay" v-show="showVideoPlayer" @click="exitVideoPlayer">
          <div class="video-container" @click.stop>
            <button class="close-button" @click="exitVideoPlayer">
              <ion-icon :icon="close"></ion-icon>
            </button>
            <video 
              ref="videoPlayer"
              oncontextmenu="return false;"
              preload="auto"
              autoPictureInPicture="true"
              playsinline
              webkit-playsinline
              x-webkit-airplay="allow"
              controls
              crossorigin="anonymous"
              @loadedmetadata="onVideoLoaded"
              @waiting="onVideoWaiting"
              @playing="onVideoPlaying"
              @pause="onVideoPaused"
              @ended="onVideoEnded"
              @error="onVideoError">
              <source :src="trackPath" :type="trackPath.endsWith('m3u8')
                ? 'application/x-mpegURL'
                : 'video/mp4'
                " />
            </video>
          </div>
        </div>

        <!-- Segment Control -->
        <ion-segment 
          v-show="hasAcademia"
          v-model="selectedListSegment" 
          mode="md" 
          class="custom-segment">
          <ion-segment-button value="all">
            <ion-label>Общие</ion-label>
          </ion-segment-button>
          <ion-segment-button value="academia">
            <ion-label>Академия</ion-label>
          </ion-segment-button>
        </ion-segment>

        <!-- Video Lists -->
        <ion-content class="list-content" :scroll-y="true">
          <!-- Common Videos -->
          <div v-show="selectedListSegment === 'all'">
            <ion-refresher slot="fixed" @ionRefresh="reloadSkills($event)">
              <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
            </ion-refresher>

            <div v-if="videoList.length" class="video-grid">
              <div 
                v-for="video in videoList" 
                :key="video.id" 
                class="video-card"
                @click="selectVideo(video.id)">
                <div class="video-thumbnail">
                  <div class="play-overlay">
                    <ion-icon :icon="play" v-if="video.id !== selectedId || !videoLoading"></ion-icon>
                    <ion-spinner v-else name="bubbles"></ion-spinner>
                  </div>
                  <img src="@/theme/liga-logo-white.png" alt="liga-logo" class="video-logo" />
                </div>
                <div class="video-info">
                  <h3 class="video-title">{{ formatVideoTitle(video.name) }}</h3>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <p>Нет доступных видео</p>
            </div>
          </div>

          <!-- Academia Videos -->
          <div v-show="selectedListSegment === 'academia'">
            <ion-refresher slot="fixed" @ionRefresh="reloadSkills($event)">
              <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
            </ion-refresher>

            <div v-if="academiaVideoList.length" class="video-grid">
              <div 
                v-for="video in academiaVideoList" 
                :key="video.id" 
                class="video-card"
                @click="selectVideo(video.id)">
                <div class="video-thumbnail">
                  <div class="play-overlay">
                    <ion-icon :icon="play" v-if="video.id !== selectedId || !videoLoading"></ion-icon>
                    <ion-spinner v-else name="bubbles"></ion-spinner>
                  </div>
                  <img src="@/theme/liga-logo-white.png" alt="liga-logo" class="video-logo" />
                </div>
                <div class="video-info">
                  <h3 class="video-title">{{ formatVideoTitle(video.name) }}</h3>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <p>Нет доступных видео</p>
            </div>
          </div>
        </ion-content>
      <!-- </div> -->
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  IonContent,
  IonIcon,
  IonLabel,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  toastController
} from '@ionic/vue';
import { RmxAudioPlayer } from 'capacitor-plugin-playlist';
import { close, play } from "ionicons/icons";
import { defineComponent, inject, ref } from 'vue';
import { useRouter } from 'vue-router';

import { GlobalContext } from '@/model/globalcontext.model';
import { GoogleMedia } from '@/model/googlemedia.model';
import AccountService from "@/services/account.service";

interface VideoPlayerMethods {
  requestFullscreen?: () => Promise<void>;
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  msRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => void;
}

export default defineComponent({
  name: 'VideoEducationPage',
  components: {
    IonContent,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonIcon,
    IonSpinner,
    IonSegment,
    IonSegmentButton,
    IonLabel,
  },
  setup() {
    const globalContext = inject("globalContext") as GlobalContext;
    const audioPlayer = inject("audioPlayer") as RmxAudioPlayer;
    const router = useRouter();
    
    const videoPlayer = ref<HTMLVideoElement & VideoPlayerMethods>();
    
    return {
      globalContext,
      audioPlayer,
      router,
      videoPlayer,
      play,
      close,
    };
  },
  data() {
    return {
      accountService: new AccountService(),
      hasAcademia: false,
      selectedListSegment: "all" as "all" | "academia",
      videoList: [] as GoogleMedia[],
      academiaVideoList: [] as GoogleMedia[],
      selectedId: '',
      videoLoading: false,
      trackPath: '',
      token: '',
      showVideoPlayer: false,
      isVideoPlaying: false,
    };
  },
  async ionViewWillEnter() {
    try {
      const response = await Preferences.get({ key: "jwt" });
      this.token = response.value || '';
      await this.getVideoList();
    } catch (error) {
      console.error('Failed to get token:', error);
      this.showErrorToast('Ошибка загрузки данных');
    }
  },
  ionViewDidLeave() {
    this.exitVideoPlayer();
  },
  mounted() {
    this.setupVideoListeners();
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  },
  methods: {
    async showErrorToast(message: string) {
      const toast = await toastController.create({
        message: message,
        duration: 3000,
        position: 'top',
        color: 'danger'
      });
      await toast.present();
    },
    async getVideoList() {
      if (this.globalContext.offlineMode) {
        this.showErrorToast('Оффлайн режим: видео недоступны');
        return;
      }

      try {
        this.hasAcademia = await this.accountService.hasAnyAuthorityAndCheckAuth("ROLE_ACADEMIA");
        
        const [commonVideos, academiaVideos] = await Promise.all([
          this.fetchVideos('Ted'),
          this.hasAcademia ? this.fetchVideos('Academia') : Promise.resolve([]),
        ]);
        
        this.videoList = commonVideos;
        this.academiaVideoList = academiaVideos;
      } catch (error) {
        console.error('Error loading videos:', error);
        this.showErrorToast('Ошибка загрузки списка видео');
      }
    },
    async fetchVideos(folder: string): Promise<GoogleMedia[]> {
      try {
        const response = await CapacitorHttp.get({
          url: `${process.env.VUE_APP_API_EXT_URL}api/google/media/${folder}`,
          headers: { Authorization: `Bearer ${this.token}` },
          readTimeout: 60000,
        });
        return response.data || [];
      } catch (error) {
        console.error(`Error fetching ${folder} videos:`, error);
        this.showErrorToast(`Ошибка загрузки видео из ${folder}`);
        return [];
      }
    },
    async selectVideo(id: string) {
      if (this.selectedId === id) {
        this.exitVideoPlayer();
        return;
      }

      this.selectedId = id;
      this.videoLoading = true;

      try {
        this.trackPath = await this.getVideoUrl(id);
        this.showVideoPlayer = true;
        
        await this.$nextTick();
        if (this.videoPlayer) {
          
          this.videoPlayer.src = this.trackPath;
          this.videoPlayer.load();
        }
      } catch (error) {
        console.error('Error loading video:', error);
        this.showErrorToast('Ошибка загрузки видео');
        this.videoLoading = false;
        this.selectedId = '';
      }
    },
    async getVideoUrl(id: string): Promise<string> {
      try {
        const response = await CapacitorHttp.get({
          url: `${process.env.VUE_APP_API_URL}api/google/video/storage/${id}`,
          headers: { Authorization: `Bearer ${this.token}` },
          readTimeout: 60000,
        });
        
        if (response.data && this.selectedId === id) {
          return `${process.env.VUE_APP_VIDEO_URL}?id=${id}`;
        }
        throw new Error('Invalid video data');
      } catch (error) {
        console.error('Error getting video URL:', error);
        throw error;
      }
    },
    setupVideoListeners() {
      if (!this.videoPlayer) return;

      const video = this.videoPlayer;

      video.addEventListener('enterpictureinpicture', () => {
        this.isVideoPlaying = true;
      });

      video.addEventListener('leavepictureinpicture', () => {
        this.isVideoPlaying = false;
      });
    },
    onVideoLoaded() {
      this.videoLoading = false;
      this.playVideo();
    },
    onVideoWaiting() {
      this.videoLoading = true;
    },
    onVideoPlaying() {
      this.videoLoading = false;
      this.isVideoPlaying = true;
      this.pauseAudioIfPlaying();
    },
    onVideoPaused() {
      this.isVideoPlaying = false;
    },
    onVideoEnded() {
      this.isVideoPlaying = false;
    },
    onVideoError(event: Event) {
      const videoElement = event.target as HTMLVideoElement;
      const error = videoElement.error;
      
      if (error) {
        console.error('Video error details:', {
          code: error.code,
          message: error.message,
          mediaError: error
        });
        
        switch(error.code) {
          case error.MEDIA_ERR_ABORTED:
            console.error('Video playback was aborted');
            break;
          case error.MEDIA_ERR_NETWORK:
            console.error('Network error occurred');
            break;
          case error.MEDIA_ERR_DECODE:
            console.error('Video decoding error');
            break;
          case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            console.error('Video format not supported');
            break;
          default:
            console.error('Unknown video error');
        }
      }
      
      this.videoLoading = false;
      this.showErrorToast('Ошибка воспроизведения видео');
      this.exitVideoPlayer();
    },
    async playVideo(retryCount = 0) {
      if (!this.videoPlayer || retryCount > 2) return;

      try {
        // For iOS, we need to trigger play in response to user gesture
        await this.videoPlayer.play().catch(e => {
          // If direct play fails, try entering fullscreen first (iOS workaround)
          if (this.videoPlayer?.webkitEnterFullscreen) {
            this.videoPlayer.webkitEnterFullscreen();
          }
          throw e;
        });
      } catch (error) {
        console.error('Error playing video:', error);
            // Retry after a delay
        setTimeout(() => {
          this.playVideo(retryCount + 1);
        }, 500);
        // If we still can't play, show an error
        this.showErrorToast('Не удалось воспроизвести видео');
        this.exitVideoPlayer();
      }
    },
    pauseAudioIfPlaying() {
      if (this.audioPlayer && this.audioPlayer.hasLoaded) {
        this.audioPlayer.pause();
      }
    },
    exitVideoPlayer() {
      if (this.videoPlayer) {
        this.videoPlayer.pause();
        
        // Exit fullscreen if in fullscreen mode
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else if (this.videoPlayer.webkitExitFullscreen) {
          this.videoPlayer.webkitExitFullscreen();
        }
      }

      this.trackPath = '';
      this.selectedId = '';
      this.showVideoPlayer = false;
      this.videoLoading = false;
      this.isVideoPlaying = false;
    },
    reloadSkills(event?: CustomEvent) {
      this.getVideoList().finally(() => {
        if (event?.detail) {
          event.detail.complete();
        }
      });
    },
    formatVideoTitle(name: string): string {
      return name ? name.split('.')[0] : '';
    },
    handleWindowResize() {
      // Handle any responsive behavior if needed
    },
  },
});
</script>

<style scoped>
.custom-segment {
  margin-bottom: 16px;
  --background: #2a2a2a;
  --color: #ffffff;
  --color-checked: #ffffff;
  --indicator-color: #4a8cff;
}

.list-content {
  --background: transparent;
  height: calc(100% - 120px);
  overflow-y: auto;
  --padding-start: 16px;
  --padding-end: 16px;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  padding-bottom: 16px;
}

.video-card {
  background: #2a2a2a;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.video-thumbnail {
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background: #1e1e1e;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.play-overlay ion-icon {
  font-size: 48px;
  color: rgba(255, 255, 255, 0.8);
}

.play-overlay ion-spinner {
  color: rgba(255, 255, 255, 0.8);
}

.video-logo {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 20%;
  max-width: 60px;
}

.video-info {
  padding: 12px;
}

.video-title {
  margin: 0;
  font-size: 1rem;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.7);
}

/* Video Overlay Styles */
.video-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container {
  width: 100%;
  max-width: 800px;
  position: relative;
  padding: 20px;
}

.video-container video {
  width: 100%;
  border-radius: 8px;
  background: #000;
}

.close-button {
  position: absolute;
  top: max(10px, env(safe-area-inset-top));
  right: max(10px, env(safe-area-inset-right));
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  cursor: pointer;
}

.close-button ion-icon {
  color: white;
  font-size: 24px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .video-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
  
  .page-container {
    padding-top: max(12px, env(safe-area-inset-top));
    padding-left: max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
  }
}

@media (max-width: 480px) {
  .video-grid {
    grid-template-columns: 1fr;
  }
}

/* Additional safe area support for iOS */
@supports (padding: max(0px)) {
  .page-container, .video-overlay, .close-button {
    padding-top: max(16px, env(safe-area-inset-top));
  }
}
</style>